import { escapeField, downloadCsv } from 'screens/Main/tabs/Send/utils/csvExport';
import { getLanguageDisplayName } from 'constants/languages';

// --- Have List ---

export interface HaveListExportItem {
  quantity: number;
  cardName: string;
  setName: string;
  setCode: string;
  collectorNumber: string | null;
  condition: string;
  language: string;
  foil: boolean;
  forTrade: boolean;
  pointValue: number | null;
}

export interface HaveListExportResponse {
  items: HaveListExportItem[];
  truncated: boolean;
}

const HAVE_LIST_CSV_HEADER = 'Quantity,Card Name,Set Name,Set Code,Collector Number,Finish,Condition,Language,Value,For Trade';

const CONDITION_ABBREVIATIONS: Record<string, string> = {
  NEAR_MINT: 'NM',
  SLIGHTLY_PLAYED: 'LP',
  MODERATELY_PLAYED: 'MP',
  HEAVILY_PLAYED: 'HP',
  DAMAGED: 'DMG',
};

function abbreviateCondition(condition: string): string {
  return CONDITION_ABBREVIATIONS[condition] || condition || '';
}

export function generateHaveListCsv(items: HaveListExportItem[]): string {
  const lines = items.map(item =>
    [
      String(item.quantity),
      escapeField(item.cardName),
      escapeField(item.setName || ''),
      escapeField((item.setCode || '').toUpperCase()),
      item.collectorNumber || '',
      item.foil ? 'Foil' : 'Nonfoil',
      abbreviateCondition(item.condition),
      getLanguageDisplayName(item.language || 'en'),
      item.pointValue != null ? String(item.pointValue) : '???',
      item.forTrade ? 'Yes' : 'No',
    ].join(',')
  );
  return [HAVE_LIST_CSV_HEADER, ...lines].join('\n');
}

// --- Want List ---

export interface WantListExportItem {
  quantity: number;
  cardName: string;
  acceptableSets: string[];
  acceptableConditions: string[];
  acceptedLanguages: string[];
  collectorNumbers: string | null;
  acceptFoil: boolean;
  acceptNonFoil: boolean;
  pointValue: number | null;
}

export interface WantListExportResponse {
  items: WantListExportItem[];
  truncated: boolean;
}

const WANT_LIST_CSV_HEADER = 'Quantity,Card Name,Sets,Collector Number,Finish,Conditions,Languages,Value';

function buildFinish(acceptFoil: boolean, acceptNonFoil: boolean): string {
  const parts: string[] = [];
  if (acceptFoil) parts.push('Foil');
  if (acceptNonFoil) parts.push('Nonfoil');
  return parts.length > 0 ? parts.join(', ') : 'Foil, Nonfoil';
}

function buildConditions(conditions: string[]): string {
  if (!conditions || conditions.length === 0) return 'Any';
  return conditions.map(c => abbreviateCondition(c)).join(', ');
}

function buildLanguages(languages: string[]): string {
  if (!languages || languages.length === 0) return 'Any';
  return languages.map(l => getLanguageDisplayName(l)).join(', ');
}

export function generateWantListCsv(items: WantListExportItem[]): string {
  const lines = items.map(item =>
    [
      String(item.quantity),
      escapeField(item.cardName),
      escapeField(item.acceptableSets?.length > 0 ? item.acceptableSets.join(', ') : 'Any'),
      escapeField(item.collectorNumbers || ''),
      escapeField(buildFinish(item.acceptFoil, item.acceptNonFoil)),
      escapeField(buildConditions(item.acceptableConditions)),
      escapeField(buildLanguages(item.acceptedLanguages)),
      item.pointValue != null ? String(item.pointValue) : '???',
    ].join(',')
  );
  return [WANT_LIST_CSV_HEADER, ...lines].join('\n');
}

// Re-export downloadCsv for convenience
export { downloadCsv };
